import type { ErrorLike } from '@apollo/client'

import { useEngagementConversationById } from '../engagement/useEngagementConversationById'
import type { MentionData } from '../../Mentions/fragments/mentionData'

export type UseMentionConversationResult = {
  /**
   * Thread root post (a `mentionType: post` mention), or null when the mention
   * has no parent post. Feeds the card's top miniature.
   */
  post: MentionData | null
  /** Context items preceding the mention, oldest first (parent at the top). */
  context: MentionData[]
  loading: boolean
  error: ErrorLike | null
}

/**
 * Mention-specific specialization over `useEngagementConversationById`.
 * Hardcodes `engagementType: 'mention'` and narrows the polymorphic
 * `Engagement` `post` / `ancestors` onto `Mention`, mirroring the wrapper
 * pattern of `useDismissMention` over `useDismissEngagement`.
 *
 * A single fetch on expand powers both the post miniature (`post`) and the
 * context section (`context`). Pass `enabled: false` to defer it until the
 * card is expanded.
 */
export const useMentionConversation = (
  mentionId: string,
  options?: { enabled?: boolean },
): UseMentionConversationResult => {
  const { post, ancestors, loading, error } = useEngagementConversationById({
    engagementId: mentionId,
    engagementType: 'mention',
    enabled: options?.enabled,
  })

  const postMention = post?.__typename === 'Mention' ? post : null

  const ancestorMentions = ancestors.flatMap((item) =>
    item.__typename === 'Mention' ? [item] : [],
  )

  // `ancestors` arrive newest first (closest to the mention). Reverse to
  // chronological order so the direct parent sits closest to the main
  // mention at the bottom — matching how `CommentsContextSection` renders
  // its `context` slice. The root post no longer appears here (it's the
  // separate `post` field), so no post-type filtering is needed.
  return {
    post: postMention,
    context: ancestorMentions.toReversed(),
    loading,
    error,
  }
}
